import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card, Btn, Modal, Field, Input, Select, Badge, EmptyState } from "../components/kr";
import { DatePicker } from "../components/DatePicker";
import { toast } from "sonner";
import { Trash, Plus, CalendarBlank, User, ShieldCheck, CheckCircle, Clock } from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";

const STATUSES = ["present", "leave", "half_day", "special_holiday"];
const STATUS_LABELS = {
  present: "Present",
  leave: "Leave",
  half_day: "Half day",
  special_holiday: "Special holiday",
  // legacy — still rendered on historical rows
  absent: "Absent",
  paid_leave: "Leave",
  casual_leave: "Leave",
};
const TONE = {
  present: "green", leave: "zinc", half_day: "yellow",
  special_holiday: "blue",
  // legacy
  absent: "red",
  paid_leave: "zinc", casual_leave: "zinc",
  sick: "zinc", casual: "zinc",
};
const LEAVE_TYPES = [
  { value: "leave", label: "Leave" },
];

export default function Attendance() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isPrivileged = user?.role === "admin" || user?.role === "production";
  const today = new Date().toISOString().slice(0, 10);
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [date, setDate] = useState(today);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [specialOpen, setSpecialOpen] = useState(false);
  const [myEmp, setMyEmp] = useState(null);
  const [meBusy, setMeBusy] = useState(false);

  // Non-privileged users are auto-redirected to their own attendance page —
  // the master sheet is admin/production-only.
  useEffect(() => {
    if (user && !isPrivileged) {
      let alive = true;
      api.get("/employees/me")
        .then(r => { if (alive && r.data?.id) nav(`/attendance/employee/${r.data.id}`, { replace: true }); })
        .catch(() => {
          toast.error("Your account isn't linked to any employee record. Ask admin to add you.");
        });
      return () => { alive = false; };
    }
  }, [user, isPrivileged, nav]);

  // Try to resolve the current user's employee record for the "My Attendance" quick link.
  useEffect(() => {
    let alive = true;
    api.get("/employees/me")
      .then(r => { if (alive) setMyEmp(r.data); })
      .catch(() => { if (alive) setMyEmp(null); });
    return () => { alive = false; };
  }, []);

  const goMyAttendance = async () => {
    if (myEmp?.id) { nav(`/attendance/employee/${myEmp.id}`); return; }
    setMeBusy(true);
    try {
      const r = await api.get("/employees/me");
      setMyEmp(r.data);
      nav(`/attendance/employee/${r.data.id}`);
    } catch (e) {
      toast.error(
        e.response?.status === 404
          ? "Your user is not linked to any employee. Ask admin to add you to Employees master with the same email."
          : (e.response?.data?.detail || "Failed"),
      );
    } finally { setMeBusy(false); }
  };

  // Guard rendering while redirect is in-flight so employees never see the master sheet.
  const dateIsSunday = React.useMemo(() => {
    try { return new Date(date + "T00:00:00").getDay() === 0; } catch { return false; }
  }, [date]);

  const load = useCallback(async (d) => {
    const [a, e] = await Promise.all([
      api.get("/attendance", { params: { date_filter: d } }),
      api.get("/employees"),
    ]);
    setItems(a.data);
    setEmployees(e.data.filter(x => x.active !== false));
  }, []);
  useEffect(() => {
    if (isPrivileged) load(date);
  }, [date, load, isPrivileged]);

  if (user && !isPrivileged) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 font-sans text-sm font-medium text-slate-500 animate-pulse" data-testid="employee-attendance-redirect">
        <Clock size={18} className="mr-2 animate-spin text-orange-500" />
        Redirecting to your personal attendance sheet…
      </div>
    );
  }

  const del = async (id) => {
    await api.delete(`/attendance/${id}`);
    load(date);
  };

  const present = items.filter(i => i.status === "present").length;
  const markedNames = new Set(items.map(i => i.worker_name));
  const unmarkedEmployees = employees.filter(e => !markedNames.has(e.name));

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10 font-sans text-slate-900 selection:bg-orange-100">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Modernized Header */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 transition-all duration-300">
          <PageHeader
            label="WORKFORCE MANAGEMENT"
            title="Attendance Hub"
            subtitle={`${present} of ${items.length} marked present · ${employees.length} active employees · ${unmarkedEmployees.length} unmarked`}
            action={
              <div className="flex gap-2.5 flex-wrap items-center">
                <Btn variant="secondary" onClick={goMyAttendance}
                     data-testid="my-attendance-btn" disabled={meBusy}
                     className="!rounded-xl border-slate-200 shadow-sm hover:bg-slate-50 hover:text-slate-950 transition-all font-medium inline-flex items-center gap-2">
                  <User size={15} weight="bold" className="text-slate-500" /> {meBusy ? "Loading…" : "My Attendance"}
                </Btn>
                {user?.role === "admin" && (
                  <Btn variant="secondary" onClick={() => setSpecialOpen(true)}
                       disabled={dateIsSunday || employees.length === 0}
                       data-testid="mark-special-holiday-btn"
                       className="!rounded-xl border-slate-200 shadow-sm hover:bg-blue-50/50 hover:text-blue-700 disabled:opacity-50 transition-all font-medium inline-flex items-center gap-2">
                    <CalendarBlank size={15} weight="bold" className="text-blue-500" /> Special Holiday
                  </Btn>
                )}
                <Btn onClick={() => setBulkOpen(true)}
                     disabled={dateIsSunday || employees.length === 0}
                     data-testid="bulk-mark-btn"
                     className="!rounded-xl !bg-slate-900 hover:!bg-slate-800 text-white shadow-md shadow-slate-900/10 disabled:opacity-50 transition-all font-medium inline-flex items-center gap-2">
                  <Plus size={15} weight="bold" /> Bulk Mark
                </Btn>
                <Btn variant="secondary" onClick={() => setLeaveOpen(true)} data-testid="request-leave-btn"
                     className="!rounded-xl border-slate-200 shadow-sm hover:bg-orange-50/50 hover:text-orange-700 transition-all font-medium inline-flex items-center gap-2">
                  <CalendarBlank size={15} weight="bold" className="text-orange-500" /> Request Leave
                </Btn>
              </div>
            }
          />
        </div>

        {/* Info Cards / Sunday Alert */}
        {employees.length === 0 ? (
          <Card className="!rounded-2xl border-dashed border-2 border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-slate-600 flex items-center gap-2">
              No active team records. Attendance tracking requires linked profile directories —{" "}
              <Link to="/employees" className="text-orange-600 hover:text-orange-700 font-semibold underline decoration-2 underline-offset-2 transition-colors">
                Setup workforce roster first
              </Link>{" "}
              to populate your roster sheet.
            </p>
          </Card>
        ) : (
          <Card className="!rounded-2xl border border-slate-200/80 bg-white shadow-sm p-4 transition-all">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <Field label="Roster Tracking Date" className="font-semibold text-slate-700">
                <DatePicker value={date} onChange={e => setDate(e.target.value)} data-testid="att-date" className="mt-1 !rounded-xl border-slate-200 focus:border-orange-500 focus:ring-orange-500/20" />
              </Field>
              {dateIsSunday && (
                <div className="flex-1 max-w-2xl px-4 py-3 bg-amber-50/80 border border-amber-200 rounded-xl text-amber-800 text-sm font-medium flex items-center gap-2.5 shadow-sm"
                     data-testid="sunday-banner">
                  <span className="text-base">🌞</span> <span>Company Holiday — Automated framework handles weekend baseline, tracking is disabled on Sundays.</span>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Individual Profile Shortcuts Grid */}
        {employees.length > 0 && (
          <Card className="!p-0 !rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden" data-testid="employee-picker-card">
            <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-sm font-bold tracking-tight text-slate-900">Staff Directories View</div>
                <div className="text-xs text-slate-500 mt-0.5 font-medium">
                  Jump directly to detailed metrics, trends, calendar records, and histories for any person.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-0 divide-y divide-x divide-slate-100">
              {employees.map(emp => (
                <Link
                  key={emp.id}
                  to={`/attendance/employee/${emp.id}`}
                  data-testid={`open-emp-${emp.id}`}
                  className="flex items-center gap-3.5 px-5 py-4 hover:bg-slate-50/80 group transition-all duration-200"
                >
                  <div className="w-10 h-10 bg-slate-900 group-hover:bg-orange-600 text-white text-xs font-bold flex items-center justify-center rounded-xl shrink-0 transition-all duration-200 shadow-sm shadow-slate-900/10">
                    {(emp.name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800 group-hover:text-slate-950 truncate transition-colors">{emp.name}</div>
                    <div className="text-[11px] font-medium text-slate-400 group-hover:text-slate-500 mt-0.5 truncate flex items-center gap-1">
                      <span className="font-mono bg-slate-100 group-hover:bg-white px-1.5 py-0.5 rounded border border-slate-200/50">{emp.emp_id || "—"}</span>
                      <span>·</span>
                      <span className="capitalize">{emp.role || "employee"}</span>
                    </div>
                  </div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 group-hover:text-orange-600 shrink-0 transition-colors flex items-center gap-0.5">
                    View <span className="transform translate-x-0 group-hover:translate-x-0.5 transition-transform">→</span>
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        )}

        {/* Master Attendance Sheet Table */}
        <Card className="!p-0 !rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
          {items.length === 0 ? (
            <div className="p-12 text-center bg-slate-50/30 rounded-2xl">
              <EmptyState 
                title="No attendance entries recorded" 
                hint={dateIsSunday ? "Sundays are automated standard rest days across all team parameters." : "Roster sheet is blank. Initialize with Bulk Mark above."} 
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-200 text-xs font-bold tracking-wider text-slate-500 uppercase">
                    <th className="px-6 py-4 font-bold">Team Member</th>
                    <th className="px-6 py-4 font-bold">Status Label</th>
                    <th className="px-6 py-4 font-bold">Logged By</th>
                    <th className="px-6 py-4 w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                  {items.map(i => {
                    const emp = employees.find(e => e.name === i.worker_name);
                    return (
                      <tr key={i.id} data-testid={`att-row-${i.id}`} className="hover:bg-slate-50/40 group transition-colors">
                        <td className="px-6 py-4 font-semibold text-slate-900">
                          {emp ? (
                            <Link to={`/attendance/employee/${emp.id}`} className="hover:text-orange-600 transition-colors flex items-center gap-2" data-testid={`emp-link-${emp.id}`}>
                              {i.worker_name}
                              {emp.role === "admin" && <ShieldCheck size={14} className="text-blue-500" title="Admin Account" />}
                            </Link>
                          ) : i.worker_name}
                        </td>
                        <td className="px-6 py-4">
                          <Badge tone={TONE[i.status] || "zinc"} className="!rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize tracking-wide shadow-sm">
                            {STATUS_LABELS[i.status] || i.status.replace("_", " ")}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 font-mono text-xs text-slate-400 font-medium">
                          {i.marked_by}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button 
                            onClick={() => del(i.id)} 
                            className="text-slate-300 hover:text-red-600 p-1 rounded-lg hover:bg-red-50 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all duration-150" 
                            data-testid={`att-del-${i.id}`}
                            title="Delete entry"
                          >
                            <Trash size={15} weight="bold" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Modals Framework Containers */}
      {bulkOpen && (
        <BulkMarkModal
          date={date}
          employees={employees}
          existing={items}
          isAdmin={user?.role === "admin"}
          onClose={() => setBulkOpen(false)}
          onSaved={() => { setBulkOpen(false); load(date); }}
        />
      )}

      {leaveOpen && (
        <LeaveRequestModal
          user={user}
          employees={employees}
          onClose={() => setLeaveOpen(false)}
          onSent={() => { setLeaveOpen(false); toast.success("Leave request sent to admin for approval"); }}
        />
      )}

      {specialOpen && (
        <SpecialHolidayModal
          date={date}
          totalEmployees={employees.length}
          onClose={() => setSpecialOpen(false)}
          onSaved={() => { setSpecialOpen(false); load(date); }}
        />
      )}
    </div>
  );
}

function LeaveRequestModal({ user, employees, onClose, onSent }) {
  const isAdmin = user?.role === "admin";
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [leaveType, setLeaveType] = useState("leave");
  const [workerName, setWorkerName] = useState(user?.name || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [balance, setBalance] = useState(null);

  const dates = React.useMemo(() => {
    if (!fromDate || !toDate) return [];
    const out = [];
    const s = new Date(fromDate);
    const e = new Date(toDate);
    if (s > e) return [];
    const cur = new Date(s);
    while (cur <= e) {
      out.push(cur.toISOString().slice(0, 10));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [fromDate, toDate]);

  useEffect(() => {
    const emp = employees.find(x => x.name === workerName);
    if (!emp) { setBalance(null); return; }
    const year = new Date(fromDate || today).getFullYear();
    api.get(`/employees/${emp.id}/leave-summary/${year}`)
      .then(r => setBalance(r.data))
      .catch(() => setBalance(null));
  }, [workerName, fromDate, employees, today]);

  const requestedDays = React.useMemo(() =>
    leaveType === "half_day" ? 0.5 * dates.length : dates.length,
  [leaveType, dates]);

  const insufficient = balance && requestedDays > (balance.remaining || 0);
  const zeroBalance = balance && (balance.remaining || 0) <= 0;

  const submit = async (e) => {
    e.preventDefault();
    if (dates.length === 0) { toast.error("Invalid date range"); return; }
    if (!workerName) { toast.error("Worker name required"); return; }
    if (insufficient) { toast.error(`Insufficient balance. Remaining: ${balance.remaining} day(s)`); return; }
    setBusy(true);
    try {
      await api.post("/requests/leave", {
        dates,
        leave_type: leaveType,
        worker_name: workerName,
        reason,
      });
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Request Time-Off Allocation">
      <form onSubmit={submit} className="grid grid-cols-2 gap-5 p-1" data-testid="leave-form">
        <Field label="Staff Member Allocation" full className="font-semibold text-slate-700">
          {!isAdmin ? (
            <div className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 shadow-inner" data-testid="leave-worker-locked">
              {user?.name}
              <span className="ml-2 text-[10px] font-mono font-medium uppercase tracking-widest text-slate-400">(Locked to Profile)</span>
            </div>
          ) : employees.length > 0 ? (
            <Select value={workerName} onChange={e => setWorkerName(e.target.value)} data-testid="leave-worker" required className="mt-1 !rounded-xl border-slate-200">
              <option value="">-- select profile --</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.name}>{emp.name} ({emp.emp_id})</option>
              ))}
              {user?.name && !employees.find(e => e.name === user.name) && (
                <option value={user.name}>{user.name} (me)</option>
              )}
            </Select>
          ) : (
            <Input value={workerName} onChange={e => setWorkerName(e.target.value)} required data-testid="leave-worker" className="mt-1 !rounded-xl border-slate-200" />
          )}
        </Field>

        {balance && (
          <div className={`col-span-2 border-l-4 rounded-xl p-4 text-xs shadow-sm transition-all ${zeroBalance ? "bg-red-50/70 border-red-500 text-red-900" : insufficient ? "bg-amber-50/70 border-amber-500 text-amber-900" : "bg-emerald-50/70 border-emerald-500 text-emerald-950"}`} data-testid="leave-balance-card">
            <div className="flex items-baseline justify-between">
              <div className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Annual Quota Metrics · CY-{balance.year}</div>
              <div className="font-mono font-black text-2xl text-slate-900">
                {balance.remaining} <span className="text-xs font-medium text-slate-400">/ {balance.annual_leave_quota} left</span>
              </div>
            </div>
            <div className="text-[11px] mt-1.5 font-mono text-slate-500">
              Utilized: {balance.used_days}d · Pending approval: {balance.pending_days}d · Selected Window: {requestedDays}d
            </div>
            {zeroBalance && (
              <div className="mt-2 text-red-700 font-bold flex items-center gap-1">🚫 Available ledger balance exhausted. Action locked.</div>
            )}
            {!zeroBalance && insufficient && (
              <div className="mt-2 text-amber-700 font-bold flex items-center gap-1">⚠ Selection metrics overflow remaining account allocation limits.</div>
            )}
          </div>
        )}

        <Field label="Leave Classification" full className="font-semibold text-slate-700">
          <Select value={leaveType} onChange={e => setLeaveType(e.target.value)} data-testid="leave-type" disabled={zeroBalance} className="mt-1 !rounded-xl border-slate-200">
            {LEAVE_TYPES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </Select>
        </Field>
        <Field label="Effective Start Date" className="font-semibold text-slate-700">
          <DatePicker value={fromDate} onChange={e => setFromDate(e.target.value)} required data-testid="leave-from" className="mt-1 !rounded-xl border-slate-200" />
        </Field>
        <Field label="Effective End Date" className="font-semibold text-slate-700">
          <DatePicker value={toDate} min={fromDate} onChange={e => setToDate(e.target.value)} required data-testid="leave-to" className="mt-1 !rounded-xl border-slate-200" />
        </Field>
        <Field label="Justification / Context Details" full className="font-semibold text-slate-700">
          <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Provide contextual narrative detail for review..." data-testid="leave-reason" className="mt-1 !rounded-xl border-slate-200" />
        </Field>
        
        <div className="col-span-2 bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs font-mono text-slate-600 shadow-inner">
          <div className="font-medium text-slate-900">Requesting structural allocation windows: <span className="font-bold text-orange-600">{dates.length} calendar day(s)</span> of <span className="font-bold text-slate-900">{STATUS_LABELS[leaveType] || leaveType}</span></div>
          <div className="text-slate-400 mt-1 text-[11px]">
            {dates.length > 0 && dates.length <= 5 && `Timeline Matrix: ${dates.join(", ")}`}
            {dates.length > 5 && `Timeline Boundary: ${dates[0]} through ${dates[dates.length - 1]}`}
          </div>
          <div className="text-slate-400 mt-1.5 border-t border-slate-200/60 pt-1.5 text-[11px] leading-relaxed">
            → System ledger inserts dynamic markers downstream post administrative approval framework validation.
          </div>
        </div>

        <div className="col-span-2 flex justify-end gap-2.5 mt-2">
          <Btn type="button" variant="secondary" onClick={onClose} className="!rounded-xl border-slate-200 font-medium">Cancel</Btn>
          <Btn type="submit" disabled={busy || zeroBalance || insufficient} data-testid="leave-submit"
               className={`!rounded-xl font-medium shadow-md shadow-slate-950/5 ${zeroBalance || insufficient ? "" : "!bg-slate-900 text-white hover:!bg-slate-800"}`}>
            {busy ? "Transmitting…" : zeroBalance ? "Allocation Blocked" : "Submit Configuration"}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

function BulkMarkModal({ date, employees, existing, isAdmin, onClose, onSaved }) {
  const statuses = React.useMemo(
    () => STATUSES.filter(s => s !== "special_holiday" || isAdmin),
    [isAdmin],
  );
  const preset = React.useMemo(() => {
    const m = new Map();
    for (const e of existing || []) m.set(e.worker_name, e.status);
    return Object.fromEntries(
      employees.map(emp => [emp.id, m.get(emp.name) || "present"])
    );
  }, [employees, existing]);
  const [rows, setRows] = React.useState(preset);
  const [busy, setBusy] = React.useState(false);

  const set = (empId, status) => setRows(r => ({ ...r, [empId]: status }));
  const setAll = (status) => setRows(Object.fromEntries(employees.map(e => [e.id, status])));

  const summary = React.useMemo(() => {
    const c = {};
    statuses.forEach(s => { c[s] = 0; });
    Object.values(rows).forEach(s => { if (s in c) c[s] += 1; });
    return c;
  }, [rows, statuses]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const entries = employees.map(emp => ({
        worker_name: emp.name,
        status: rows[emp.id] || "present",
      }));
      const r = await api.post("/attendance/bulk", { date, entries });
      toast.success(`Saved · ${r.data.created} new + ${r.data.updated} updated`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose}
           title={`Batch Matrix Update · ${new Date(date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}`}
           size="lg">
      <form onSubmit={submit} data-testid="bulk-attendance-form" className="p-1 space-y-5">
        
        {/* Bulk Actions Bar */}
        <div className="flex flex-wrap items-center gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200/60">
          <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase font-mono ml-1">Batch Apply Baseline:</span>
          <div className="flex flex-wrap gap-1.5">
            {statuses.map(s => (
              <button key={s} type="button" onClick={() => setAll(s)}
                      data-testid={`set-all-${s}`}
                      className="px-2.5 py-1 bg-white hover:bg-slate-900 hover:text-white border border-slate-200 rounded-lg text-xs font-bold tracking-wide transition-all shadow-sm">
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>

        {/* Live Metrics Display counters */}
        <div className={`grid grid-cols-2 ${statuses.length === 4 ? "md:grid-cols-4" : "md:grid-cols-3"} gap-3 text-center`} data-testid="bulk-summary">
          {statuses.map(s => (
            <div key={s} className={`border border-slate-200/80 rounded-xl p-3.5 shadow-sm transition-all duration-300 ${{
              present: "bg-emerald-50/50 border-emerald-200/80 text-emerald-950",
              leave: "bg-slate-50 border-slate-200 text-slate-900",
              half_day: "bg-amber-50/50 border-amber-200/80 text-amber-950",
              special_holiday: "bg-blue-50/50 border-blue-200/80 text-blue-950",
            }[s]}`}>
              <div className="text-[10px] font-bold uppercase tracking-wider opacity-60">{STATUS_LABELS[s]}</div>
              <div className="font-mono font-black text-2xl mt-1">{summary[s]}</div>
            </div>
          ))}
        </div>

        {/* Roster Inputs Table Container */}
        <div className="border border-slate-200/80 rounded-xl max-h-[400px] overflow-y-auto shadow-sm bg-white">
          <table className="w-full text-left border-collapse">
            <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10 text-xs font-bold uppercase text-slate-500">
              <tr>
                <th className="px-5 py-3.5 font-bold">Roster Personnel</th>
                {statuses.map(s => <th key={s} className="text-center px-4 py-3.5 font-bold">{STATUS_LABELS[s]}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm text-slate-800">
              {employees.map(emp => (
                <tr key={emp.id} data-testid={`bulk-row-${emp.emp_id}`} className="hover:bg-slate-50/30 transition-colors">
                  <td className="px-5 py-3">
                    <div className="font-semibold text-slate-900">{emp.name}</div>
                    <div className="text-[10px] font-mono text-slate-400 mt-0.5">{emp.emp_id} · <span className="capitalize">{emp.role}</span></div>
                  </td>
                  {statuses.map(s => (
                    <td key={s} className="text-center px-4 py-3">
                      <div className="inline-flex items-center justify-center p-1 rounded-full hover:bg-slate-100/80 transition-all">
                        <input
                          type="radio"
                          name={`bulk-${emp.id}`}
                          checked={rows[emp.id] === s}
                          onChange={() => set(emp.id, s)}
                          data-testid={`bulk-radio-${emp.emp_id}-${s}`}
                          className="accent-slate-900 w-4 h-4 cursor-pointer focus:ring-offset-0 focus:ring-0"
                        />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Action Controls */}
        <div className="flex justify-end gap-2.5 pt-2 border-t border-slate-100">
          <Btn type="button" variant="secondary" onClick={onClose} className="!rounded-xl border-slate-200 font-medium">Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="bulk-submit"
               className="!rounded-xl !bg-slate-900 hover:!bg-slate-800 text-white font-medium shadow-md shadow-slate-950/5">
            {busy ? "Committing records…" : `Commit ${employees.length} Positions`}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

function SpecialHolidayModal({ date, totalEmployees, onClose, onSaved }) {
  const [notes, setNotes] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/attendance/special-holiday", { date, notes: notes.trim() });
      toast.success(`Special Holiday marked · ${r.data.created} new + ${r.data.updated} updated (${r.data.total_employees} employees)`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark special holiday");
    } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Establish Structural Company Holiday" data-testid="special-holiday-modal">
      <form onSubmit={submit} className="space-y-5 p-1" data-testid="special-holiday-form">
        <div className="bg-blue-50/60 border-l-4 border-blue-500 rounded-xl p-4 text-xs text-blue-950 shadow-sm">
          <div className="font-bold uppercase tracking-wider text-[10px] text-blue-800/80 mb-1 font-mono">Operations Framework Directives</div>
          <div className="leading-relaxed">
            Applies a universal <span className="font-bold text-blue-900">Special Holiday status token</span> across all 
            {" "}<span className="font-bold text-blue-900">{totalEmployees} working roster records</span> mapped to the target date 
            {" "}<span className="font-mono bg-white px-1.5 py-0.5 border border-blue-200 text-blue-900 font-bold rounded">{date}</span>. 
            This configuration executes as paid workspace parameters within the payroll framework. Restricted exclusively to administrative rights.
          </div>
        </div>
        <Field label="Holiday Descriptor Context (Optional)" className="font-semibold text-slate-700">
          <Input value={notes} onChange={e => setNotes(e.target.value)}
                 placeholder="e.g. Diwali Festival, National Observance, Founders Memorial Day"
                 data-testid="special-holiday-notes" 
                 className="mt-1 !rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500/10" />
        </Field>
        <div className="flex justify-end gap-2.5 pt-2">
          <Btn type="button" variant="secondary" onClick={onClose} className="!rounded-xl border-slate-200 font-medium">Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="special-holiday-submit"
               className="!rounded-xl !bg-blue-600 hover:!bg-blue-700 text-white font-medium shadow-md shadow-blue-600/10">
            {busy ? "Registering framework…" : `Deploy for ${totalEmployees} Staff Records`}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}